import type { SchemaOption } from "../types";

export type QuestionMarkerState =
  | "upcoming"
  | "in-progress"
  | "answered"
  | "warning"
  | "error";

/**
 * Which marker state a question is in — computed fresh on every render from its own answer/error/
 * warning/open-ness, never stored. Priority order matters: an answer that also triggered an
 * eligibility warning shows the warning, not the checkmark, and a hard input error always wins over
 * both (Figma 679:28260 / 678:28178's own component notes).
 */
export function questionMarkerState({
  hasError,
  hasWarning,
  hasValue,
  isOpen,
}: {
  hasError: boolean;
  hasWarning: boolean;
  hasValue: boolean;
  isOpen: boolean;
}): QuestionMarkerState {
  if (hasError) return "error";
  if (hasWarning) return "warning";
  if (hasValue) return "answered";
  if (isOpen) return "in-progress";
  return "upcoming";
}

/**
 * Whether a value counts as "answered" for the question-row marker and the collapsed preview.
 *
 * ⚠️ `0` and `false` DO count. `residency_confirmed` is a real boolean field where `false` ("Non,
 * je vis à l'étranger") is a deliberate, meaningful answer — not an absent one — and a numeric
 * field's empty state is `NaN`/`undefined`, never `0`. Only `undefined`/`null`/an empty string/an
 * empty array mean "nothing here yet".
 */
export function hasAnswerValue(value: unknown): boolean {
  if (value === undefined || value === null) return false;
  if (typeof value === "string") return value.trim() !== "";
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

/**
 * The truncated preview shown on a COLLAPSED, answered row (Figma 679:28260's "Répondue" state).
 *
 * Resolves through `options` first — a select/radio/multi-select answer previews as its LABEL, not
 * its wire value (`residency_confirmed: true` must read "Oui", not "true"). Anything without a
 * matching option (free text, a number, or a value the options list doesn't carry) previews as
 * itself. Truncation to Figma's ~200px is left to CSS (`truncate`) rather than a character count,
 * so it degrades correctly across locales instead of cutting mid-word at a fixed length.
 */
export function formatAnswerPreview(
  value: unknown,
  options: readonly SchemaOption[]
): string | null {
  if (!hasAnswerValue(value)) return null;

  const labelFor = (raw: unknown): string => {
    const match = options.find((option) => option.value === raw);
    return match ? match.label : String(raw);
  };

  if (Array.isArray(value)) {
    return value.map(labelFor).join(", ");
  }
  return labelFor(value);
}
